/**
 * The settings dialog (NEWS-297; kerf UI, NM-NBQ3P4).
 *
 * A kerf UI `DialogSurface`, a native `<dialog>` (the same contract as
 * `appDialog` in `dialogs.tsx`): kerf UI owns the modal behaviour, the store
 * owns `settingsOpen`, and the dialog `close` listener in `app.tsx` clears it. Its
 * two sections are a `SegmentedControl` — a small exclusive choice that swaps
 * the panel below it — rather than ARIA tabs.
 *
 * | Section | Answers |
 * |---|---|
 * | App | everything about the app itself: account, plan, notifications, location, appearance, setup |
 * | Data | *what* is kept, and how do I get it out (or in) |
 *
 * Rendering only. Every `data-action` written here is handled by one
 * `delegate()` in `app.tsx` (NEWS-126).
 */

import { Badge } from '@kerfjs/ui/badge';
import { Button } from '@kerfjs/ui/button';
import { deviceClass } from '@kerfjs/ui/device-class';
import { FilePickerTrigger } from '@kerfjs/ui/file-picker';
import { Grid } from '@kerfjs/ui/grid';
import { List } from '@kerfjs/ui/list';
import { ListHeader } from '@kerfjs/ui/list-header';
import { Row } from '@kerfjs/ui/row';
import { SegmentedControl } from '@kerfjs/ui/segmented-control';
import { StateBanner } from '@kerfjs/ui/state-banner';
import { DialogSurface } from '@kerfjs/ui/surface-scaffold';
import { Switch } from '@kerfjs/ui/switch';
import { Text } from '@kerfjs/ui/text';
import { TextField } from '@kerfjs/ui/text-field';
import { ValueTable, ValueTableRow } from '@kerfjs/ui/value-table';
import type { SafeHtml } from 'kerfjs';

import type { AppState } from '../../app/stores.js';
import { appStore, readOnboardingSeen } from '../../app/stores.js';
import { icon } from '../../components/icons.js';
import { themeTileSvg } from '../../components/theme-tiles.js';
import { canUpgrade } from '../topics/capacity.js';
import { IMPORT_STORIES_PICKER, IMPORT_TOPICS_PICKER } from './import-files.js';

/**
 * Theme choices (FR-3.74, NEWS-334, NM-25). `auto` is first and the default —
 * a real choice rather than the absence of one; its tile draws what it does.
 */
const THEME_OPTIONS: { label: string; value: 'auto' | 'light' | 'dark' }[] = [
  { label: 'Auto', value: 'auto' },
  { label: 'Light', value: 'light' },
  { label: 'Dark', value: 'dark' },
];

/** The two sections (NEWS-118; no Schedule tab since NM-11, no Source tab since NM-12). */
const SETTINGS_TABS = [
  { value: 'app', label: 'App' },
  { value: 'data', label: 'Data' },
] as const;

/**
 * Why notifications are blocked, and where to actually fix it (NEWS-40): in a
 * browser the permission belongs to the *browser*, per site, so the note names
 * the origin and warns off macOS System Settings, which lists only browsers.
 */
function notifyBlockedNoteJsx(): SafeHtml {
  return (
    <StateBanner
      tone="warning"
      icon={icon('warn')}
      copyLayout="stacked"
      title="Notifications Are Blocked"
      detail={`Your browser is blocking notifications for ${location.origin}. Fix it in the browser’s own site settings for this page — the padlock or icon beside the address bar. Looking in macOS System Settings won’t help: in a browser the permission belongs to the browser, so it lists Chrome or Safari and never Newsmonger.`}
    />
  );
}

/** A section heading inside the dialog — a level-3 heading under the dialog title. */
function section(label: string): SafeHtml {
  return <ListHeader label={label} size="xs" headingLevel={3} />;
}

/**
 * The Data section. Topics **above** stories (NEWS-327): a topic is what you
 * own, a story is what it produced. Each pairs an export with the import that
 * reads it back (FR-30.15), and its delete-all sits with the data it removes
 * (NM-24) as a quiet button — the confirmation carries the warning (NM-40).
 *
 * Exports are fetch-blob buttons (NM-101), not `<a download>`, so they can
 * say "Exporting…" and toast the result. Imports are kerf UI
 * `FilePickerTrigger`s: a label around a hidden file input, so a click or a
 * key reaches the file chooser with no script; their `change` is delegated.
 */
function dataPanelJsx(s: AppState): SafeHtml {
  return (
    <List gap="m" rootAttributes={{ 'data-settings-panel': 'data' }}>
      {section('Topics')}
      <Row gap="xs" wrap controlInsets="rl" rootAttributes={{ 'data-io-row': 'topics' }}>
        <Button
          emphasis="secondary"
          disabled={s.exporting}
          rootAttributes={{ 'data-export-url': '/api/export-topics.json' }}
          icon={icon('download')}
        >
          {s.exporting ? 'Exporting…' : 'Export Topics…'}
        </Button>
        <FilePickerTrigger
          label="Import Topics…"
          name="import-topics"
          pickerId={IMPORT_TOPICS_PICKER}
          accept="application/json,.json"
          icon={icon('upload')}
        />
      </Row>
      <Row controlInsets="rl">
        <Button
          emphasis="plain"
          tone="danger"
          action="clear-topics"
          disabled={s.topics.length === 0}
          icon={icon('delete')}
        >
          Delete All Topics
        </Button>
      </Row>
      {section('Stories')}
      <Row gap="xs" wrap controlInsets="rl" rootAttributes={{ 'data-io-row': 'stories' }}>
        {/* `download`, not `share` (NEWS-161): this writes a file to disk. */}
        <Button tone="brand" action="open-export" icon={icon('download')}>
          Export Stories…
        </Button>
        <FilePickerTrigger
          label="Import Stories…"
          name="import-stories"
          pickerId={IMPORT_STORIES_PICKER}
          accept="application/json,.json"
          icon={icon('upload')}
        />
      </Row>
      <Row controlInsets="rl">
        <Button
          emphasis="plain"
          tone="danger"
          action="clear-stories"
          disabled={s.feedTotal === 0}
          icon={icon('delete')}
        >
          Delete All Stories
        </Button>
      </Row>
    </List>
  );
}

/** The App section. */
function appPanelJsx(s: AppState): SafeHtml {
  const user = s.session?.user ?? null;
  return (
    <List gap="m" rootAttributes={{ 'data-settings-panel': 'app' }}>
      {/* Signed-in account (NM-20): who you are, and sign out back to the gate. */}
      {section('Account')}
      {user === null ? null : (
        <ValueTable label="Account">
          <ValueTableRow label="Name" value={user.displayName} />
          <ValueTableRow label="Email" value={user.email} />
          {s.plan === null ? null : (
            <ValueTableRow
              label="Plan"
              value={
                <Badge size="compact" tone="info">
                  {s.plan.label}
                </Badge>
              }
            />
          )}
        </ValueTable>
      )}
      <Row gap="xs" wrap controlInsets="rl" rootAttributes={{ 'data-account-actions': '' }}>
        {user === null ? null : (
          <Button emphasis="secondary" action="sign-out">
            Sign Out
          </Button>
        )}
        {/* Plan upgrade (NM-225/226): only with mock billing on and a pricier tier. */}
        {s.plan !== null && s.billing.enabled && canUpgrade(s.billing, s.plan.id) ? (
          <Button tone="brand" action="open-checkout">
            Upgrade
          </Button>
        ) : null}
      </Row>
      {s.plan !== null && s.billing.enabled && !canUpgrade(s.billing, s.plan.id) ? (
        <Text tone="quiet" size="compact" data-account-plan-top="">
          You’re on the top plan.
        </Text>
      ) : null}
      {section('Notifications')}
      <List controlInsets="rl">
        <Switch
          action="notify-toggle"
          checked={s.settings.notifyOnNewItems}
          label="Notify me when new stories arrive while Newsmonger isn’t focused"
        />
      </List>
      {/* Always present: the blocked-permission note appears in it (KF-377). */}
      <div data-notify-note="">{s.notifyPermissionDenied ? notifyBlockedNoteJsx() : ''}</div>
      {/* Location (NEWS-393, FR-35): a fact about the *user*. Free text — there is
          no place list and nothing validates it (FR-35.2), in any script. */}
      {section('Location')}
      <List controlInsets="rl">
        <TextField
          name="location"
          label="Where You Are"
          value={s.settings.location}
          placeholder="e.g. Lisbon · Portugal · 東京 · the Scottish Borders"
          spellcheck={false}
          autocorrect="off"
          hint="Used only for topics about somewhere — local events, property, schools, transport, national politics. Global subjects ignore it. As broad or as precise as you like; leaving it empty keeps every topic global."
        />
      </List>
      {/* Appearance (FR-3.74, NM-25): a visual picker — three tiles, each a
          figurative Newsmonger window painted in that theme's own palette, so a
          reader sees what they are choosing. Native radios inside labels give
          keyboard operation and names for free; the tiles are app-owned
          artwork, so their frame styling is app CSS (`.theme-tile*`). */}
      {section('Appearance')}
      <List controlInsets="rl">
        <div role="radiogroup" aria-label="Theme" data-theme-picker="">
          <Grid columns={3} gap="xs">
            {THEME_OPTIONS.map((o) => (
              <label class="theme-tile" data-key={o.value}>
                <input
                  class="theme-tile-input"
                  type="radio"
                  name="theme"
                  data-action="theme"
                  data-theme={o.value}
                  value={o.value}
                  checked={o.value === s.settings.theme ? true : undefined}
                />
                <span class="theme-tile-frame">{themeTileSvg(o.value)}</span>
                {/* A check on the selected tile so the choice doesn't rely on the
                  ring's colour alone (NM-95); revealed by CSS from the live radio. */}
                <span class="theme-tile-label">
                  <span class="theme-tile-check" aria-hidden="true">
                    {icon('ok', 12)}
                  </span>
                  {o.label}
                </span>
              </label>
            ))}
          </Grid>
        </div>
      </List>
      {/* The setup guide (NM-26) — "again" only when this account has seen it (NM-39). */}
      {section('Setup')}
      <Row controlInsets="rl">
        <Button tone="brand" action="rerun-onboarding">
          {readOnboardingSeen(s.installId, s.session?.user?.id ?? '')
            ? 'Show the Setup Guide Again'
            : 'Show the Setup Guide'}
        </Button>
      </Row>
    </List>
  );
}

/** The viewport's device class (kerf UI), read during render so the presentation follows it. */
const device = deviceClass();

export function settingsDialogJsx(): SafeHtml {
  const s = appStore.state.value;
  // A centred modal on a roomy screen; a full-screen sheet on a phone (NM-94),
  // where a centred modal would be squeezed edge to edge over the dimmed feed.
  return (
    <DialogSurface
      label="Settings"
      rootAttributes={{ 'data-key': 'dialog-settings', 'data-dialog': 'settings' }}
      size="medium"
      maxHeight="viewport"
      presentation={device.value.handset ? 'fullscreen' : 'modal'}
      lightDismiss
    >
      <List gap="m" rootAttributes={{ 'data-dialog-body': '' }}>
        <Row controlInsets="rl">
          <SegmentedControl
            id="settings-tabs"
            label="Settings Sections"
            action="settings-tab"
            value={s.settingsTab}
            choices={SETTINGS_TABS}
            layout="equal"
          />
        </Row>
        {s.settingsTab === 'data' ? dataPanelJsx(s) : appPanelJsx(s)}
      </List>
    </DialogSurface>
  );
}
